import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '@/api/client';
import { authApi, type UserRole } from '@/api/auth';

interface AuthState {
  user: string | null;
  role: UserRole | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  const apply = (me: { authenticated: boolean; username: string | null; role: UserRole | null }) => {
    if (me.authenticated) {
      setUser(me.username);
      setRole(me.role);
    } else {
      setUser(null);
      setRole(null);
    }
  };

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      apply(me);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        apply({ authenticated: false, username: null, role: null });
      } else {
        // Network error / server down — treat as not logged in but log it
        console.error('auth.me failed', e);
        apply({ authenticated: false, username: null, role: null });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Listen for "auth:check" events fired by the api client when ANY non-auth
  // /api/** call returns 401/403. Debounced so a page that fires N parallel
  // requests post-deploy doesn't trigger N /api/auth/me calls. Refresh()
  // either confirms the user is still authenticated (forbidden = inline error)
  // or flips user→null (RequireAuth then redirects to /login).
  useEffect(() => {
    let pending = false;
    const onCheck = () => {
      if (pending) return;
      pending = true;
      setTimeout(() => {
        pending = false;
        void refresh();
      }, 100);
    };
    window.addEventListener('auth:check', onCheck);
    return () => window.removeEventListener('auth:check', onCheck);
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const me = await authApi.login(username, password);
    apply(me);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    apply({ authenticated: false, username: null, role: null });
  }, []);

  return (
    <AuthContext.Provider value={{ user, role, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
